import React, { useState, useEffect } from 'react';
import { trasferteAPI, commesseAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { 
  Plus, 
  Plane,
  Hotel,
  Utensils,
  Car,
  Receipt,
  Loader2,
  Trash2,
  Pencil,
  AlertCircle,
  Check,
  X
} from 'lucide-react';

const CATEGORIA_OPTIONS = [
  { value: 'viaggio', label: 'Viaggio', icon: Car },
  { value: 'alloggio', label: 'Alloggio', icon: Hotel },
  { value: 'vitto', label: 'Vitto', icon: Utensils },
  { value: 'altro', label: 'Altro', icon: Receipt },
];

const getCategoriaIcon = (categoria) => {
  const opt = CATEGORIA_OPTIONS.find(o => o.value === categoria);
  return opt?.icon || Receipt;
};

export const TrasfertaSection = ({ selectedDate, onUpdate }) => {
  const { user } = useAuth();
  const [trasferte, setTrasferte] = useState([]);
  const [commesseDisponibili, setCommesseDisponibili] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [budgetInfo, setBudgetInfo] = useState({});

  const [formData, setFormData] = useState({
    data: '',
    commessa_id: '',
    categoria: 'viaggio',
    importo: '',
    descrizione: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedDate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Carica commesse con budget trasferte dove l'utente è assegnato
      const commesseRes = await commesseAPI.getAll();
      const commesseConBudget = commesseRes.data.filter(c => {
        const budgetTrasferte = c.budget_trasferte || [];
        const totale = budgetTrasferte.reduce((acc, b) => acc + (b.costo_previsto || 0), 0);
        return totale > 0 && c.stato === 'attiva';
      });
      setCommesseDisponibili(commesseConBudget);

      // Carica trasferte del giorno selezionato
      if (selectedDate) {
        const trasferteRes = await trasferteAPI.getAll({ 
          data_da: selectedDate, 
          data_a: selectedDate 
        });
        setTrasferte(trasferteRes.data);
      }
    } catch (error) {
      console.error('Error loading trasferte data:', error);
      toast.error('Errore nel caricamento delle trasferte');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBudgetInfo = async (commessaId) => {
    if (!commessaId) return;
    try {
      const res = await trasferteAPI.getDisponibili(commessaId);
      setBudgetInfo(prev => ({ ...prev, [commessaId]: res.data }));
    } catch (error) {
      console.error('Error loading budget info:', error);
    }
  };

  const openAddDialog = () => {
    setEditingEntry(null);
    setFormData({
      data: selectedDate || new Date().toISOString().split('T')[0],
      commessa_id: '',
      categoria: 'viaggio',
      importo: '',
      descrizione: ''
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      data: entry.data,
      commessa_id: entry.commessa_id,
      categoria: entry.categoria,
      importo: entry.importo.toString(),
      descrizione: entry.descrizione || ''
    });
    loadBudgetInfo(entry.commessa_id);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.commessa_id || !formData.importo) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        data: formData.data,
        commessa_id: formData.commessa_id,
        categoria: formData.categoria,
        importo: parseFloat(formData.importo),
        descrizione: formData.descrizione || null
      };

      if (editingEntry) {
        await trasferteAPI.update(editingEntry.id, submitData);
        toast.success('Trasferta aggiornata');
      } else {
        await trasferteAPI.create(submitData);
        toast.success('Trasferta inserita');
      }

      setIsDialogOpen(false);
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving trasferta:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ') 
        : (typeof detail === 'string' ? detail : 'Errore nel salvataggio');
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa trasferta?')) return;
    
    try {
      await trasferteAPI.delete(entryId);
      toast.success('Trasferta eliminata');
      loadData();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting trasferta:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ') 
        : (typeof detail === 'string' ? detail : 'Errore nell\'eliminazione');
      toast.error(errorMsg);
    }
  };

  const selectedCommessa = commesseDisponibili.find(c => c.id === formData.commessa_id);
  const currentBudgetInfo = budgetInfo[formData.commessa_id];

  if (isLoading) {
    return <Skeleton className="h-32" />;
  }

  return (
    <Card className="mt-6" data-testid="trasferte-section">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5" />
            Trasferte del Giorno
          </CardTitle>
          <CardDescription>
            Rendiconta le spese di trasferta per il {selectedDate ? formatDate(selectedDate) : 'giorno selezionato'}
          </CardDescription>
        </div>
        {commesseDisponibili.length > 0 && (
          <Button size="sm" onClick={openAddDialog} data-testid="add-trasferta-btn">
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi Trasferta
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {commesseDisponibili.length === 0 ? (
          <p className="text-center py-4 text-muted-foreground">
            Nessuna commessa con budget trasferte disponibile per te
          </p>
        ) : trasferte.length === 0 ? (
          <p className="text-center py-4 text-muted-foreground">
            Nessuna trasferta registrata per questo giorno
          </p>
        ) : (
          <div className="space-y-3">
            {trasferte.map((entry) => {
              const CatIcon = getCategoriaIcon(entry.categoria);
              return (
                <div 
                  key={entry.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  data-testid={`trasferta-${entry.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-muted">
                      <CatIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm text-muted-foreground">
                          {entry.commessa_codice}
                        </span>
                        <Badge variant={
                          entry.stato === 'approvato' ? 'default' : 
                          entry.stato === 'respinto' ? 'destructive' : 'secondary'
                        }>
                          {entry.stato === 'approvato' ? 'Approvata' : 
                           entry.stato === 'respinto' ? 'Respinta' : 'In attesa'}
                        </Badge>
                      </div>
                      <p className="font-medium">{entry.commessa_nome}</p>
                      <p className="text-sm text-muted-foreground capitalize">{entry.categoria}</p>
                      {entry.descrizione && (
                        <p className="text-sm text-muted-foreground">{entry.descrizione}</p>
                      )}
                      {entry.motivo_rifiuto && (
                        <p className="text-sm text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {entry.motivo_rifiuto}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-mono text-xl font-bold">{formatCurrency(entry.importo)}</p>
                    {entry.stato !== 'approvato' && (
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditDialog(entry)}
                          data-testid={`edit-trasferta-${entry.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(entry.id)}
                          data-testid={`delete-trasferta-${entry.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-md" data-testid="trasferta-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Modifica Trasferta' : 'Nuova Trasferta'}
            </DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Modifica i dati della trasferta' : 'Inserisci i dati della trasferta'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="trasferta-data">Data *</Label>
              <Input
                id="trasferta-data"
                type="date"
                value={formData.data}
                onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                required
                data-testid="trasferta-data-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trasferta-commessa">Commessa *</Label>
              <Select
                value={formData.commessa_id}
                onValueChange={(value) => {
                  setFormData(prev => ({ ...prev, commessa_id: value }));
                  loadBudgetInfo(value);
                }}
              >
                <SelectTrigger id="trasferta-commessa" data-testid="trasferta-commessa-select">
                  <SelectValue placeholder="Seleziona commessa" />
                </SelectTrigger>
                <SelectContent>
                  {commesseDisponibili.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.codice_commessa} - {c.nome_progetto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentBudgetInfo && (
                <div className="text-xs mt-2 p-2 bg-muted rounded">
                  <div className="flex justify-between">
                    <span>Budget: {formatCurrency(currentBudgetInfo.budget_totale)}</span>
                    <span>Utilizzato: {formatCurrency(currentBudgetInfo.consuntivo_totale)}</span>
                  </div>
                  <Progress 
                    value={currentBudgetInfo.budget_totale > 0 
                      ? (currentBudgetInfo.consuntivo_totale / currentBudgetInfo.budget_totale * 100) 
                      : 0
                    } 
                    className="h-1.5 mt-2"
                  />
                  <p className="mt-1">
                    Disponibile: <span className={
                      currentBudgetInfo.disponibile >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }>{formatCurrency(currentBudgetInfo.disponibile)}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="trasferta-categoria">Categoria *</Label>
              <Select
                value={formData.categoria}
                onValueChange={(value) => setFormData(prev => ({ ...prev, categoria: value }))}
              >
                <SelectTrigger id="trasferta-categoria" data-testid="trasferta-categoria-select">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIA_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="flex items-center gap-2">
                        <opt.icon className="h-4 w-4" />
                        {opt.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="trasferta-importo">Importo (€) *</Label>
              <Input
                id="trasferta-importo"
                type="number"
                step="0.01"
                min="0"
                value={formData.importo}
                onChange={(e) => setFormData(prev => ({ ...prev, importo: e.target.value }))}
                placeholder="0.00"
                required
                data-testid="trasferta-importo-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trasferta-descrizione">Descrizione</Label>
              <Textarea
                id="trasferta-descrizione"
                value={formData.descrizione}
                onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                placeholder="Dettagli della spesa..."
                rows={2}
                data-testid="trasferta-descrizione-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="trasferta-submit-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingEntry ? 'Salva Modifiche' : 'Registra Trasferta'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TrasfertaSection;
